import { useLiveQuery } from 'dexie-react-hooks';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { LearnerPreferences, LearnerProfile } from '@/domain/records';
import { AppStateContext, type AppState } from './useAppState';
import { db } from '@/services/db/database';
import { updateLearner } from '@/services/db/learners';
import {
  DEFAULT_DEVICE_SETTINGS,
  getDeviceSettings,
  setActiveLearnerId,
  updateDeviceSettings,
  type DeviceSettings,
} from '@/services/db/meta';

/** Live view of local data: any write to IndexedDB re-renders the consumers. */
export function AppStateProvider({ children }: { children: ReactNode }) {
  const learners = useLiveQuery(() => db.learners.orderBy('createdAt').toArray(), []);
  const settings = useLiveQuery(() => getDeviceSettings(), []);
  const activeId = useLiveQuery(
    async () => ((await db.meta.get('activeLearnerId'))?.value as string | undefined) ?? null,
    [],
  );

  // The selection applies immediately in memory; storage (and live queries) follow.
  const [selection, setSelection] = useState<{
    id: string | undefined;
    profile?: LearnerProfile;
  } | null>(null);
  const currentId = selection ? selection.id : (activeId ?? undefined);

  const ready = learners !== undefined && settings !== undefined && activeId !== undefined;
  const activeLearner =
    learners?.find((l) => l.id === currentId) ??
    (selection?.profile?.id === currentId ? selection?.profile : undefined);

  const selectLearner = useCallback(async (learner: string | LearnerProfile | undefined) => {
    const id = typeof learner === 'string' ? learner : learner?.id;
    setSelection({ id, ...(typeof learner === 'object' ? { profile: learner } : {}) });
    await setActiveLearnerId(id);
  }, []);
  const updateSettings = useCallback(async (patch: Partial<DeviceSettings>) => {
    await updateDeviceSettings(patch);
  }, []);
  const updatePreferences = useCallback(
    async (patch: Partial<LearnerPreferences>) => {
      if (!activeLearner) return;
      await updateLearner(activeLearner.id, {
        preferences: { ...activeLearner.preferences, ...patch },
      });
    },
    [activeLearner],
  );

  // Accessibility preferences of the active learner apply to the whole page.
  const largeText = activeLearner?.preferences.largeText ?? false;
  const reducedMotion = activeLearner?.preferences.reducedMotion ?? false;
  useEffect(() => {
    const root = document.documentElement;
    if (largeText) root.dataset.text = 'large';
    else delete root.dataset.text;
    if (reducedMotion) root.dataset.motion = 'reduced';
    else delete root.dataset.motion;
  }, [largeText, reducedMotion]);

  const value = useMemo<AppState>(
    () => ({
      ready,
      settings: settings ?? DEFAULT_DEVICE_SETTINGS,
      learners: learners ?? [],
      ...(activeLearner ? { activeLearner } : {}),
      selectLearner,
      updateSettings,
      updatePreferences,
    }),
    [ready, settings, learners, activeLearner, selectLearner, updateSettings, updatePreferences],
  );

  return <AppStateContext.Provider value={value}>{children}</AppStateContext.Provider>;
}
